import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { analyticsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area,
} from 'recharts';
import {
  ChartLine, Briefcase, FileText, Package, Users, TrendUp,
  CircleNotch, ArrowUp, ArrowDown, Percent, CurrencyDollar,
} from '@phosphor-icons/react';

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#eab308', '#06b6d4', '#ec4899', '#6366f1', '#14b8a6'];

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'orange', testId }) => (
  <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">{title}</p>
          <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{value}</p>
          {subtitle && <p className="text-xs text-zinc-400 dark:text-zinc-500">{subtitle}</p>}
        </div>
        <div className={`p-2.5 bg-${color}-100 dark:bg-${color}-900/30 rounded-none`}>
          <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} weight="bold" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ConversionCard = ({ label, rate, numerator, denominator, testId }) => (
  <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid={testId}>
    <CardContent className="p-5 flex flex-col items-center text-center">
      <div className="relative w-20 h-20 mb-3">
        <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="3" className="text-zinc-200 dark:text-zinc-700" />
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="3"
            className="text-orange-500"
            strokeDasharray={`${rate} ${100 - rate}`}
            strokeLinecap="butt"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-zinc-900 dark:text-zinc-100">
          {rate}%
        </span>
      </div>
      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{label}</p>
      <p className="text-xs text-zinc-400 mt-1">{numerator} de {denominator}</p>
    </CardContent>
  </Card>
);

const MONTH_LABELS = {
  '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr', '05': 'May', '06': 'Jun',
  '07': 'Jul', '08': 'Ago', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic',
};

const formatMonth = (m) => {
  const parts = m.split('-');
  return `${MONTH_LABELS[parts[1]] || parts[1]} ${parts[0].slice(2)}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-3 shadow-lg">
      <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-xs" style={{ color: entry.color }}>
          {entry.name}: <span className="font-bold">{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

const AnalyticsPage = () => {
  const { user, isAdmin, isBoth, activeRole } = useAuth();
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [categories, setCategories] = useState([]);
  const [conversion, setConversion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, t, cat, conv] = await Promise.all([
          analyticsAPI.getSummary(),
          analyticsAPI.getTimeline(),
          analyticsAPI.getCategories(),
          analyticsAPI.getConversion(),
        ]);
        setSummary(s.data);
        setTimeline(t.data);
        setCategories(cat.data);
        setConversion(conv.data);
      } catch (e) {
        console.error('Analytics load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="analytics-loading">
        <CircleNotch className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  const showClient = isAdmin || isBoth || user?.role === 'cliente';
  const showProvider = isAdmin || isBoth || user?.role === 'proveedor';
  const clientData = summary?.client;
  const providerData = summary?.provider;
  const globalData = summary?.global;

  // Merge timeline data into a single array
  const allMonths = new Set();
  timeline?.projects?.forEach(d => allMonths.add(d.month));
  timeline?.quotations?.forEach(d => allMonths.add(d.month));
  timeline?.proposals?.forEach(d => allMonths.add(d.month));
  const sortedMonths = [...allMonths].sort();
  const timelineData = sortedMonths.map(m => {
    const proj = timeline?.projects?.find(d => d.month === m);
    const quot = timeline?.quotations?.find(d => d.month === m);
    const prop = timeline?.proposals?.find(d => d.month === m);
    return {
      month: formatMonth(m),
      Proyectos: proj?.count || 0,
      Cotizaciones: quot?.count || 0,
      Propuestas: prop?.count || 0,
    };
  });

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
            <ChartLine className="w-6 h-6 text-orange-500" /> Analytics
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            {isAdmin ? 'Vista global de la plataforma' : 'Métricas de tu actividad'}
          </p>
        </div>
        {isBoth && (
          <Badge variant="outline" className="rounded-none text-sm">
            Vista: {activeRole === 'cliente' ? 'Cliente' : 'Proveedor'} + Global
          </Badge>
        )}
      </div>

      {/* Global Stats (Admin only) */}
      {isAdmin && globalData && (
        <div>
          <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" /> Plataforma Global
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="global-stats">
            <StatCard title="Usuarios" value={globalData.total_users} icon={Users} color="blue" testId="stat-users" />
            <StatCard title="Proyectos" value={globalData.total_projects} icon={Briefcase} color="emerald" testId="stat-projects-global" />
            <StatCard title="Cotizaciones" value={globalData.total_quotations} subtitle={`${globalData.active_quotations} activas`} icon={FileText} color="orange" testId="stat-quotations-global" />
            <StatCard title="Propuestas" value={globalData.total_proposals} subtitle={`${globalData.accepted_proposals} aceptadas`} icon={Package} color="violet" testId="stat-proposals-global" />
          </div>
        </div>
      )}

      {/* Client Stats */}
      {showClient && clientData && (
        <div>
          <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mb-3 flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            {isBoth || isAdmin ? 'Como Cliente' : 'Mis Proyectos'}
            {isBoth && <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="client-stats">
            <StatCard title="Mis Proyectos" value={clientData.my_projects} subtitle={`${clientData.draft_projects} borradores`} icon={Briefcase} color="emerald" testId="stat-my-projects" />
            <StatCard title="Cotizaciones" value={clientData.my_quotations} subtitle={`${clientData.active_quotations} activas`} icon={FileText} color="orange" testId="stat-my-quotations" />
            <StatCard title="Completadas" value={clientData.completed_quotations} icon={TrendUp} color="blue" testId="stat-completed" />
            <StatCard title="Propuestas Recibidas" value={clientData.proposals_received} icon={Package} color="violet" testId="stat-proposals-received" />
          </div>
        </div>
      )}

      {/* Provider Stats */}
      {showProvider && providerData && (
        <div>
          <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mb-3 flex items-center gap-2">
            <Package className="w-4 h-4" />
            {isBoth || isAdmin ? 'Como Proveedor' : 'Mis Propuestas'}
            {isBoth && <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="provider-stats">
            <StatCard title="Propuestas Enviadas" value={providerData.my_proposals} subtitle={`${providerData.sent_proposals} pendientes`} icon={Package} color="orange" testId="stat-my-proposals" />
            <StatCard title="Aceptadas" value={providerData.accepted_proposals} icon={TrendUp} color="emerald" testId="stat-accepted" />
            <StatCard title="Seleccionadas" value={providerData.selected_proposals} icon={ArrowUp} color="blue" testId="stat-selected" />
            <StatCard title="Ingresos" value={`$${Number(providerData.total_revenue).toLocaleString('es-MX')}`} icon={CurrencyDollar} color="emerald" testId="stat-revenue" />
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Timeline Chart */}
        {timelineData.length > 0 && (
          <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="timeline-chart">
            <CardHeader className="border-b border-zinc-200 dark:border-zinc-700 pb-3">
              <CardTitle className="text-base">Actividad por Mes</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={timelineData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Proyectos" fill="#10b981" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Cotizaciones" fill="#f97316" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Propuestas" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Categories Pie Chart */}
        {categories.length > 0 && (
          <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="categories-chart">
            <CardHeader className="border-b border-zinc-200 dark:border-zinc-700 pb-3">
              <CardTitle className="text-base">Top Categorías de Materiales</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={categories.slice(0, 6)}
                    dataKey="count"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={45}
                    paddingAngle={2}
                    label={({ category, percent }) => `${category.slice(0, 12)}${category.length > 12 ? '...' : ''} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#a1a1aa', strokeWidth: 1 }}
                  >
                    {categories.slice(0, 6).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Conversion Metrics */}
      {conversion && (
        <div>
          <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mb-3 flex items-center gap-2">
            <Percent className="w-4 h-4" /> Tasas de Conversión
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="conversion-stats">
            <ConversionCard
              label="Tasa de Respuesta"
              rate={conversion.quotation_response_rate}
              numerator={conversion.quotations_with_proposals}
              denominator={conversion.total_quotations_analyzed}
              testId="conversion-response"
            />
            <ConversionCard
              label="Tasa de Cierre"
              rate={conversion.quotation_completion_rate}
              numerator={conversion.completed_quotations}
              denominator={conversion.total_quotations_analyzed}
              testId="conversion-completion"
            />
            <ConversionCard
              label="Aceptación de Propuestas"
              rate={conversion.proposal_acceptance_rate}
              numerator={conversion.accepted_proposals}
              denominator={conversion.total_proposals_analyzed}
              testId="conversion-acceptance"
            />
          </div>
        </div>
      )}

      {/* Top Categories Table */}
      {categories.length > 0 && (
        <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="categories-table">
          <CardHeader className="border-b border-zinc-200 dark:border-zinc-700 pb-3">
            <CardTitle className="text-base">Detalle de Categorías</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {categories.map((cat, i) => (
                <div key={cat.category} className="flex items-center justify-between px-5 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center text-xs font-bold text-white rounded-none" style={{ backgroundColor: COLORS[i % COLORS.length] }}>
                      {i + 1}
                    </span>
                    <span className="text-sm text-zinc-900 dark:text-zinc-100">{cat.category}</span>
                  </div>
                  <Badge variant="outline" className="rounded-none font-mono">{cat.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsPage;
